import { skipToken, useQuery, useSuspenseQuery } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { useEffect, useMemo } from "react";

import {
  GetEntitiesDocument,
  type GetEntitiesQuery,
  type GetEntitiesQueryVariables,
  GetEntityListGeoItemsDocument,
  type GetEntityListGeoItemsQuery,
  type GetEntityListGeoItemsQueryVariables,
} from "~/features/entities/gqls";
import type { EntityListFilters, EntitySortOrder, SortDirection } from "~/features/entities/types";
import { buildEntitiesVariables, toFirstGeoItemIdByRecordId } from "~/features/entities/utils/transformers";
import { useTenant } from "~/features/tenant/context";

interface UseEntityListArgs {
  filters: EntityListFilters;
  sortOrder: EntitySortOrder;
  direction: SortDirection;
  pageIndex: number;
  pageSize: number;
}

/**
 * Server-paginated list of entity records for the current tenant. Variables
 * (where / orderBy / limit / offset) are built from the URL-derived filters.
 * Keeps the V1 `tenantId` variable (see plan — explicit decision).
 */
export function useEntityList({ filters, sortOrder, direction, pageIndex, pageSize }: UseEntityListArgs) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  const list = useSuspenseQuery<GetEntitiesQuery, GetEntitiesQueryVariables>(GetEntitiesDocument, {
    variables: buildEntitiesVariables({ tenantId, filters, sortOrder, direction, pageIndex, pageSize }),
  });
  const recordIds = useMemo(() => list.data.records.map(({ id }) => id), [list.data.records]);
  const geoItems = useQuery<GetEntityListGeoItemsQuery, GetEntityListGeoItemsQueryVariables>(
    GetEntityListGeoItemsDocument,
    recordIds.length > 0 ? { variables: { tenantId, recordIds } } : skipToken,
  );
  const response =
    geoItems.error || geoItems.dataState !== "complete" ? undefined : geoItems.data.cnutGetRecordGeoItemsByRecordIds;
  const validationErrorMessage = response?.__typename === "ValidationError" ? response.message : undefined;

  useEffect(() => {
    if (geoItems.error) {
      Sentry.captureException(geoItems.error);
    } else if (validationErrorMessage) {
      Sentry.captureException(new Error(validationErrorMessage));
    }
  }, [geoItems.error, validationErrorMessage]);

  const firstGeoItemIdByRecordId = useMemo(
    () =>
      toFirstGeoItemIdByRecordId(response?.__typename === "RecordGeoItemsByRecordIdsOutput" ? response.records : []),
    [response],
  );

  const refetch = async () => {
    const result = await list.refetch();
    const refetchedRecordIds = result.data?.records.map(({ id }) => id) ?? [];
    if (refetchedRecordIds.length > 0) {
      await geoItems.refetch({ tenantId, recordIds: refetchedRecordIds }).catch(() => undefined);
    }
    return result;
  };

  return { ...list, refetch, firstGeoItemIdByRecordId };
}
